/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getSdk } from "@cosmos/hygraph-schema/dist/generated/resolvers.hygraph";

import { getHygraph, getHygraphHeaders } from "./hygraph";
import { toHygraphSlug } from "./slug-prefix";

import type { OwnerFilterConfig, OwnerQueryVariant } from "./types/owner.types";
import type { WfRefApplicationOwnerFragment } from "@cosmos/hygraph-schema";

/** Cache entry with TTL support */
interface CacheEntry {
	data: WfRefApplicationOwnerFragment[];
	expiresAt: number;
}

/**
 * Module-level cache for application owners, keyed by preview status.
 * Uses separate cache entries for preview vs production to prevent data mixing.
 * Includes TTL-based expiration to handle serverless environment persistence.
 */
const ownerCache = new Map<string, CacheEntry>();

/**
 * In-flight request promises to prevent duplicate fetches.
 * Note: No race condition exists here - JavaScript is single-threaded, so the
 * synchronous check-and-set between get() and set() cannot be interleaved.
 */
const pendingRequests = new Map<string, Promise<WfRefApplicationOwnerFragment[]>>();

/** Cache key for non-preview requests */
const DEFAULT_CACHE_KEY = "default";

/** Cache TTL: 1 hour is reasonable since owner config rarely changes and deploys clear cache */
const CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * Internal function to fetch owners from Hygraph and update cache.
 */
async function fetchAndCacheOwners(
	preview: string | undefined,
	cacheKey: string,
): Promise<WfRefApplicationOwnerFragment[]> {
	try {
		const sdk = getSdk(getHygraph(preview));
		const headers = getHygraphHeaders(preview);

		const response = await sdk.WfRefApplicationOwners({}, headers);
		const owners = response.wfRefApplicationOwners ?? [];

		ownerCache.set(cacheKey, {
			data: owners,
			expiresAt: Date.now() + CACHE_TTL_MS,
		});

		return owners;
	} catch (error) {
		console.error("[Owner Filter] Failed to fetch application owners:", {
			error: error instanceof Error ? error.message : error,
			preview: preview ? "enabled" : "disabled",
			cacheKey,
		});
		// Graceful degradation: return [] so pages render unfiltered rather than 500.
		// Sitemaps still fail safely since content queries themselves would throw.
		return [];
	}
}

/**
 * Fetches and caches WfRefApplicationOwners.
 * Uses module-level caching with preview-aware keys and TTL expiration
 * to prevent stale data in serverless environments.
 * Implements request deduplication to prevent duplicate fetches during cold starts.
 *
 * @param preview - Preview param for Hygraph
 * @returns Promise with array of application owners
 */
export async function getApplicationOwners(preview?: string): Promise<WfRefApplicationOwnerFragment[]> {
	const cacheKey = preview || DEFAULT_CACHE_KEY;

	// Check for in-flight request to prevent duplicate fetches
	const pending = pendingRequests.get(cacheKey);
	if (pending) {
		return pending;
	}

	// Check cache
	const cached = ownerCache.get(cacheKey);
	if (cached && cached.expiresAt > Date.now()) {
		return cached.data;
	}

	// Create new request and store promise for deduplication
	const request = fetchAndCacheOwners(preview, cacheKey);
	pendingRequests.set(cacheKey, request);

	try {
		return await request;
	} finally {
		pendingRequests.delete(cacheKey);
	}
}

/**
 * Clears the cached application owners.
 * Useful for testing or when owners are updated.
 *
 * @param preview - Optional preview key to clear. If omitted, clears all caches.
 */
export function clearOwnerCache(preview?: string): void {
	if (preview !== undefined) {
		ownerCache.delete(preview || DEFAULT_CACHE_KEY);
	} else {
		ownerCache.clear();
	}
}

/**
 * Finds an owner by ownerId.
 *
 * @param ownerId - The owner ID to find
 * @param preview - Preview param for Hygraph
 * @returns Promise with the owner or undefined
 */
export async function findOwnerById(
	ownerId: string,
	preview?: string,
): Promise<WfRefApplicationOwnerFragment | undefined> {
	const owners = await getApplicationOwners(preview);
	return owners.find((owner) => owner.ownerId === ownerId);
}

/**
 * Determines if the given ownerId is the default owner.
 *
 * @param ownerId - The owner ID to check
 * @param preview - Preview param for Hygraph
 * @returns Promise<boolean> - true if ownerId matches the default owner
 */
export async function isDefaultOwner(ownerId: string, preview?: string): Promise<boolean> {
	const owners = await getApplicationOwners(preview);
	const defaultOwner = owners.find((owner) => owner.isDefault === true);

	return defaultOwner?.ownerId === ownerId;
}

/**
 * Builds owner filter configuration based on ownerId.
 * Includes pageSlugPrefix for slug transformation.
 *
 * @param ownerId - From HYGRAPH_OWNER_ID env var (undefined = no filter)
 * @param preview - Preview param for Hygraph
 * @returns Promise<OwnerFilterConfig>
 */
export async function getOwnerFilterConfig(ownerId: string | undefined, preview?: string): Promise<OwnerFilterConfig> {
	if (!ownerId) {
		return {
			ownerId: undefined,
			isDefaultOwner: false,
			pageSlugPrefix: undefined,
		};
	}

	const owner = await findOwnerById(ownerId, preview);

	if (!owner) {
		return {
			ownerId,
			isDefaultOwner: false,
			pageSlugPrefix: undefined,
		};
	}

	return {
		ownerId,
		isDefaultOwner: owner.isDefault === true,
		pageSlugPrefix: owner.pageSlugPrefix,
	};
}

/**
 * Determines which query variant to use based on owner config.
 * Used to select the appropriate GraphQL query.
 *
 * @param config - Owner filter configuration
 * @returns OwnerQueryVariant - "WithDefaultOwner" | "WithOwner" | "NoOwner"
 */
export function getOwnerQueryVariant(config: OwnerFilterConfig): OwnerQueryVariant {
	if (!config.ownerId) {
		return "NoOwner";
	}
	if (config.isDefaultOwner) {
		return "WithDefaultOwner";
	}
	return "WithOwner";
}

/**
 * Gets HYGRAPH_OWNER_ID from environment.
 * Returns undefined if not set (for websites like unrealengine).
 *
 * @returns string | undefined - The owner ID or undefined
 */
export function getEnvOwnerId(): string | undefined {
	return process.env.HYGRAPH_OWNER_ID || undefined;
}

/**
 * Convenience function that combines getEnvOwnerId and getOwnerFilterConfig.
 * Reads HYGRAPH_OWNER_ID from environment and fetches the full owner config.
 *
 * @param preview - Preview param for Hygraph
 * @returns Promise<OwnerFilterConfig> - Owner config from environment, or empty config if not set
 */
export async function getOwnerConfigFromEnv(preview?: string): Promise<OwnerFilterConfig> {
	const envOwnerId = getEnvOwnerId();
	return getOwnerFilterConfig(envOwnerId, preview);
}

/**
 * Resolves a URL slug to a Hygraph slug by applying the owner's page slug prefix.
 * Combines getOwnerConfigFromEnv + validation warning + toHygraphSlug into a single call.
 *
 * @param slug - The URL path from the browser (e.g., "/home")
 * @param preview - Preview param for Hygraph
 * @returns The Hygraph slug with owner prefix applied (or unchanged if no owner)
 */
export async function resolveOwnerSlug(slug: string, preview?: string): Promise<string> {
	const ownerConfig = await getOwnerConfigFromEnv(preview);

	if (ownerConfig.ownerId && !ownerConfig.pageSlugPrefix && !ownerConfig.isDefaultOwner) {
		console.warn(
			`Owner "${ownerConfig.ownerId}" not found in Hygraph or missing pageSlugPrefix - content may not load correctly`,
		);
	}

	return toHygraphSlug(slug, ownerConfig.pageSlugPrefix);
}
